/**
 * Text on a Path — draft (session-only) state.
 *
 * WMPCM-9514 Story 1 is authoring-only: opening the action bar, typing, and previewing the text
 * along the object's outline. Nothing here is persisted. The draft deliberately does NOT live on
 * `ShapeData`:
 *
 *  - The wire has no extensible bag for it. `Content` is a `oneof` (a photo fill evicts text), and
 *    `editorShape` is silently dropped by the backend — trusting that key caused two app-wide
 *    outages (see `larry-frontend-ui/CLAUDE.md` Known Gaps #17 and #40).
 *  - WMPCM-9542 (Story 5) needs commit-on-dismiss / discard-on-ESC, which requires a draft-vs-
 *    committed split regardless. Keeping the draft beside the shape rather than on it means ESC is
 *    "drop the object" instead of "restore a snapshot".
 *
 * Field names map onto the yb360 `<TOP>` wire element (`larry-common-library/src/xmls/xml_text_on_path_content.go`)
 * so the later stories don't have to rename anything:
 *
 *   startDistance → SS (start segment) + SP (distance into that segment)
 *   offset        → TO   (WMPCM-9535, Story 4)
 *   clockwise     → AN   (WMPCM-9530, Story 3)
 *
 * The type fields (`fontFamilyId` / `fontId` / `fontSizePt` / `colorHex` / `tracking` / `kerning`)
 * became operable in WMPCM-9522 (Story 2), `offset` in WMPCM-9535 (Story 4), and `clockwise` in
 * WMPCM-9530 (Story 3) — every field on the config is now driven by a real control.
 *
 * `clockwise` is consumed at render time by `resolveTextOnPathDirection`
 * (`lib/shapes/text-on-path-direction.ts`), which is also where the reason a closed outline and an
 * open path flip differently is written down.
 */

import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  DEFAULT_FONT_FAMILY_ID,
  DEFAULT_FONT_ID,
  DEFAULT_KERNING,
  DEFAULT_TRACKING,
} from '@/lib/text/constants/text-defaults'
import { letterSpacingCss } from '@/lib/text/canvas-font-metrics'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'

import { TEXT_ON_PATH_DEFAULT_COLOR } from '@/lib/shapes/text-on-path-colors'
import { TextOnPathConfig } from '@/lib/shapes/types/text-on-path-config'
import type { TextOnPathDraft } from './types/text-on-path-draft'
import type { TextOnPathTypography } from './types/text-on-path-typography'
import {
  TEXT_ON_PATH_DEFAULT_OFFSET_PT,
  TEXT_ON_PATH_DEFAULT_POINT_SIZE,
} from './constants/text-on-path-session-defaults'

export function createTextOnPathConfig(): TextOnPathConfig {
  return {
    text: '',
    startDistance: 0,
    offset: TEXT_ON_PATH_DEFAULT_OFFSET_PT,
    clockwise: true,
    fontFamilyId: DEFAULT_FONT_FAMILY_ID,
    fontId: DEFAULT_FONT_ID,
    fontSizePt: TEXT_ON_PATH_DEFAULT_POINT_SIZE,
    colorHex: TEXT_ON_PATH_DEFAULT_COLOR,
    tracking: DEFAULT_TRACKING,
    kerning: DEFAULT_KERNING,
  }
}

/**
 * Seed a draft for `shape` — from its existing text-on-path when it has one, so reopening the bar
 * shows what is already there instead of silently wiping it.
 */
export function createTextOnPathDraft(shape: ShapeData): TextOnPathDraft {
  // Merged OVER the defaults rather than spread bare: a config created before a field existed (an
  // undo snapshot restored mid-session, a hot-reloaded dev session) would otherwise arrive with
  // that field `undefined` — `createTextOnPathConfig()` is only consulted when `textOnPath` is
  // absent entirely — and its control would render empty with nothing to enumerate from.
  return { ...createTextOnPathConfig(), ...(shape.textOnPath ?? {}), shapeId: shape.id }
}

/**
 * Every field of a `TextOnPathConfig`, listed as a value the compiler keeps in step with the type.
 *
 * The `Record<keyof TextOnPathConfig, true>` annotation is the load-bearing part: add a field to
 * the type and this object stops compiling until the field is listed here. That turns what used to
 * be a warning comment on `textOnPathConfigsEqual` — "every field added MUST be compared here" —
 * into an error, which matters because the failure it guards against is silent in every other way:
 * an uncompared field reads as "nothing changed", so an edit touching only that field is discarded
 * on dismiss and pushes no undo entry, with `tsc`, `eslint` and the whole suite still green.
 */
const TEXT_ON_PATH_CONFIG_FIELDS: Record<keyof TextOnPathConfig, true> = {
  text: true,
  startDistance: true,
  offset: true,
  clockwise: true,
  fontFamilyId: true,
  fontId: true,
  fontSizePt: true,
  colorHex: true,
  tracking: true,
  kerning: true,
}

const TEXT_ON_PATH_CONFIG_KEYS = Object.keys(TEXT_ON_PATH_CONFIG_FIELDS) as Array<
  keyof TextOnPathConfig
>

/**
 * Field-wise equality — the ONE definition of "this text-on-path changed".
 *
 * Two callers depend on it, and on it being the same answer in both places:
 *
 *  - the commit skips a no-op write, so dismissing the bar without touching anything neither
 *    enqueues an element update nor pushes an undo entry the user would have to press Ctrl+Z twice
 *    to get past;
 *  - `handleShapeCommitted` decides from the same answer whether its undo patch carries
 *    `textOnPath` at all (WMPCM-9554).
 *
 * That second caller used to run its own `JSON.stringify` comparison instead. The two agreed only
 * because every config happens to be built in one field order today — a spread or a rebuild in a
 * different order compares unequal as JSON while being value-identical, which would push an undo
 * entry that restores nothing and reads to the user as a dead Ctrl+Z.
 *
 * Comparison is `===` per field, correct only because every field is a primitive. The `Record`
 * above is what forces a reader back here if that ever stops being true.
 */
export function textOnPathConfigsEqual(
  a: TextOnPathConfig | null | undefined,
  b: TextOnPathConfig | null | undefined
): boolean {
  if (a == null || b == null) return a == null && b == null

  return TEXT_ON_PATH_CONFIG_KEYS.every((key) => a[key] === b[key])
}

/**
 * What a dismiss should store: the draft stripped of its `shapeId`, or `undefined` when the text is
 * blank. Blank means "no text on this path" — committing an empty string instead would leave the
 * shape carrying an invisible config that still costs a wire field and still reopens as "not empty".
 */
export function textOnPathConfigToCommit(draft: TextOnPathDraft): TextOnPathConfig | undefined {
  if (!hasTextOnPathContent(draft)) return undefined

  const { shapeId: _shapeId, ...config } = draft

  return config
}

/**
 * Clamp a start position into `[0, pathLength]`.
 *
 * `pathLength` arrives from `SVGGeometryElement.getTotalLength()` on the rendered path, so it can
 * legitimately be 0 (a degenerate path) and is NaN in any environment without SVG geometry — jsdom
 * implements neither `getTotalLength` nor `getPointAtLength`. Both collapse to 0 rather than
 * propagating NaN into `startOffset`, which would blank the whole `<textPath>`.
 */
export function clampStartDistance(value: number, pathLength: number): number {
  if (!Number.isFinite(value)) return 0
  if (!Number.isFinite(pathLength) || pathLength <= 0) return 0
  if (value < 0) return 0
  if (value > pathLength) return pathLength
  return value
}

/**
 * Where the blue start handle sits, as a distance along the FORWARD path (WMPCM-10320 Bug 2).
 *
 * A gap short of the text's first character rather than exactly on it. The handle is a filled
 * screen-sized disc drawn over the preview, so centring it on the start position put it squarely on
 * top of the first glyph — QA could not read the letter it was anchored to. Backing it off by its
 * own radius leaves its trailing edge just touching where the text begins, which is what the legacy
 * demo does (`TEXT_HANDLE_GAP = 8` for its 16px handle: "px before the first character, so the 16px
 * handle doesn't cover it").
 *
 * `gap` is therefore expected in PATH units already counter-scaled by canvas zoom, so the clearance
 * stays constant on screen as the handle itself does.
 *
 * `runsForward` is false when the text reads against the path's own direction — a reversed open
 * path (`resolveTextOnPathDirection`'s `mirrored`). "Before the first character" is then the other
 * way along the path, and getting this wrong would move the handle INTO the text rather than off
 * it, doubling the bug instead of fixing it.
 *
 * Clamped into the path: on a closed outline the ideal position wraps past the start, and there is
 * no wrap to follow here, so it pins to the near end rather than reporting a distance that
 * `getPointAtLength` would clamp differently.
 */
export function textOnPathHandleDistance({
  textStartDistance,
  pathLength,
  gap,
  runsForward,
}: {
  textStartDistance: number
  pathLength: number
  gap: number
  runsForward: boolean
}): number {
  const start = clampStartDistance(textStartDistance, pathLength)

  if (!Number.isFinite(pathLength) || pathLength <= 0) return 0
  const safeGap = Number.isFinite(gap) && gap > 0 ? gap : 0
  const shifted = runsForward ? start - safeGap : start + safeGap

  return Math.min(Math.max(shifted, 0), pathLength)
}

/**
 * True when there is text worth drawing. Whitespace-only is treated as empty.
 *
 * A type predicate so a caller that needs the config itself after the check — the wire writer in
 * `stowed-element-builder.ts`, say — does not have to repeat the null test to satisfy the compiler.
 */
export function hasTextOnPathContent(
  config: TextOnPathConfig | null | undefined
): config is TextOnPathConfig {
  return config != null && config.text.trim().length > 0
}

/**
 * Turns a config into the attributes the `<text>` node draws with.
 *
 * Pure, and takes the font lookup as an argument rather than reading a context, so it is testable
 * without a provider — the same shape `resolveCanvasFont` (`lib/text/canvas-font-metrics.ts`) uses.
 * Family/weight/style resolution deliberately mirrors that function so a text-on-path glyph and a
 * shape-text glyph of the same `fontId` are drawn by the same rules.
 *
 * `letterSpacing` goes through `letterSpacingCss`, which is the single definition of the
 * tracking+kerning → one-channel convention. Do not re-derive `(t + k) / 1000` at the call site.
 */
export function resolveTextOnPathTypography(
  config: TextOnPathConfig,
  fonts?: ParagraphBlockFontLookup
): TextOnPathTypography {
  const cssFamily = fonts?.getFamilyCssName(config.fontFamilyId)
  const variant = fonts?.getVariantByFontId(config.fontId)
  // NOT floored at `CANVAS_FONT_MIN_PT`. That floor exists so `ctx.measureText` stays reliable at
  // small sizes and the caller scales the result back down — it is a MEASUREMENT bound, not a
  // drawing one. Applying it here would draw 6pt and 7pt text at 7.5pt while the Size control read
  // 6, and the toolbar's own minimum is 6 (`FONT_SIZE_MIN`). Only a non-finite or non-positive
  // size falls back, so `letterSpacingCss` can never be handed a NaN to multiply.
  const fontSize =
    Number.isFinite(config.fontSizePt) && config.fontSizePt > 0
      ? config.fontSizePt
      : TEXT_ON_PATH_DEFAULT_POINT_SIZE

  return {
    fontFamily: cssFamily ? `"${cssFamily}", sans-serif` : 'sans-serif',
    fontWeight: variant?.bold ? 700 : 400,
    fontStyle: variant?.italic ? 'italic' : 'normal',
    fontSize,
    letterSpacing: letterSpacingCss(config.tracking, config.kerning, fontSize),
    fill: config.colorHex,
  }
}

/**
 * Nearest distance-along-path to an arbitrary point, by coarse scan then binary refinement.
 *
 * Used by the red start dot's drag: the pointer never lands exactly on the outline, so "where did
 * they drop it" has to be projected back onto the path. Takes the sampler as an argument instead of
 * a DOM node so the search itself is pure and testable — the caller passes
 * `(len) => path.getPointAtLength(len)`, which does not exist in jsdom.
 *
 * `coarseSamples` bounds the initial scan; each refinement pass then shrinks the bracket around
 * the winner by a third, so accuracy is about `2 * pathLength / coarseSamples * (2/3) ** refine`
 * — well under a point for any realistic page object, at ~104 sampler calls per drag frame.
 */
export function nearestLengthOnPath(
  pathLength: number,
  target: { x: number; y: number },
  pointAt: (length: number) => { x: number; y: number },
  coarseSamples = 64,
  refineIterations = 20
): number {
  if (!Number.isFinite(pathLength) || pathLength <= 0) return 0
  if (!Number.isFinite(target.x) || !Number.isFinite(target.y)) return 0

  const samples = Math.max(2, Math.floor(coarseSamples))
  const step = pathLength / samples
  const distanceSquaredAt = (length: number) => {
    const p = pointAt(length)
    const dx = p.x - target.x
    const dy = p.y - target.y

    return dx * dx + dy * dy
  }

  let bestLength = 0
  let bestDistance = Number.POSITIVE_INFINITY

  for (let i = 0; i <= samples; i += 1) {
    const length = Math.min(i * step, pathLength)
    const distance = distanceSquaredAt(length)

    if (distance < bestDistance) {
      bestDistance = distance
      bestLength = length
    }
  }

  let low = Math.max(0, bestLength - step)
  let high = Math.min(pathLength, bestLength + step)

  for (let i = 0; i < refineIterations; i += 1) {
    const midLow = low + (high - low) / 3
    const midHigh = high - (high - low) / 3

    if (distanceSquaredAt(midLow) <= distanceSquaredAt(midHigh)) high = midHigh
    else low = midLow
  }

  return clampStartDistance((low + high) / 2, pathLength)
}
